// Fires & cooking (M13 Phase 3): matches light a campfire outdoors or one of
// the stoves found in building kitchens. Raw meat and toxic-raw forage roast
// into their cooked variants while you stand at a lit fire.
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Vector3, Color3 } from '@babylonjs/core/Maths/math';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';

const BURN_S = 300; // a fire burns for 5 minutes
// A lit fire is not a one-shot, it is a low STEADY beacon: the infected drift
// toward it for as long as it burns (docs/INFECTED.md Z27, PING.campfire = 25 m).
// Re-raised on this cadence rather than every frame — a ping only lives 3 s.
const CRACKLE_S = 2.5;
const CRACKLE_MAX = 4; // fires pinged per cadence; the token bucket is shared

export class Cooking {
  constructor(scene, terrain, buildings, sfx) {
    this.scene = scene;
    this.terrain = terrain;
    this.buildings = buildings;
    this.sfx = sfx;
    this.fires = []; // {x, y, z, lit (s left), flame, base, stove}
    this._flameMat = null;
    this.noise = null;   // NoiseSystem, wired in main.js (Z118)
    this.inventory = null; // Inventory, wired in main.js — a strike costs a match
    this._crackleT = 0;
    this._noise = null;  // one reused noise buffer for the ignition sound
  }

  _mats() {
    if (this._flameMat) return;
    this._flameMat = new StandardMaterial('fireMat', this.scene);
    this._flameMat.emissiveColor = Color3.FromHexString('#ff8c28');
    this._flameMat.diffuseColor = Color3.Black();
    this._flameMat.disableLighting = true;
    this._flameMat.alpha = 0.85;
    this.stoneMat = new StandardMaterial('fireStone', this.scene);
    this.stoneMat.diffuseColor = Color3.FromHexString('#5a5a56');
    this.stoneMat.emissiveColor = this.stoneMat.diffuseColor.scale(0.15);
    this.stoneMat.specularColor = Color3.Black();
    this.logMat = new StandardMaterial('fireLog', this.scene);
    this.logMat.diffuseColor = Color3.FromHexString('#4a3624');
    this.logMat.emissiveColor = this.logMat.diffuseColor.scale(0.15);
    this.logMat.specularColor = Color3.Black();
  }

  _flame(x, y, z) {
    const f = MeshBuilder.CreateCylinder('flame', { height: 0.55, diameterBottom: 0.4, diameterTop: 0.02, tessellation: 6 }, this.scene);
    f.position.set(x, y + 0.3, z);
    f.material = this._flameMat;
    f.isPickable = false;
    return f;
  }

  // build the visual + fire entry (shared by local lighting and net sync)
  _place(x, y, z, stove, litSeconds) {
    this._mats();
    let flame;
    const parts = [];
    if (stove) {
      flame = this._flame(x, y + 0.75, z);
    } else {
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        const s = MeshBuilder.CreateSphere('fstone', { diameterX: 0.22, diameterY: 0.16, diameterZ: 0.22, segments: 3 }, this.scene);
        s.position.set(x + Math.sin(a) * 0.42, y + 0.05, z + Math.cos(a) * 0.42);
        s.material = this.stoneMat; s.isPickable = false;
        parts.push(s);
      }
      for (let i = 0; i < 3; i++) {
        const log = MeshBuilder.CreateCylinder('flog', { height: 0.55, diameter: 0.07, tessellation: 5 }, this.scene);
        log.position.set(x, y + 0.12, z);
        log.rotation.set(1.25, (i / 3) * Math.PI * 2, 0);
        log.material = this.logMat; log.isPickable = false;
        parts.push(log);
      }
      flame = this._flame(x, y, z);
    }
    flame.setEnabled(litSeconds > 0);
    const f = { x, y, z, lit: litSeconds, flame, base: parts, stove };
    this.fires.push(f);
    return f;
  }

  // light a fire: at a nearby stove if there is one, otherwise a campfire on
  // open ground. Returns a user-facing message.
  light(playerPos, heading, inventory = this.inventory) {
    this._mats();
    // Lighting is not free. A dead ring of embers used to relight for nothing,
    // forever, and no path here ever consumed a match. With no inventory wired
    // (old call signature) the old free behaviour is kept rather than locking
    // the player out of fire entirely.
    const match = this._findMatch(inventory);
    if (inventory && !match) return { ok: false, msg: 'You need matches to light a fire' };
    // an existing dead fire nearby? relight it
    for (const f of this.fires) {
      if (Math.hypot(f.x - playerPos.x, f.z - playerPos.z) < 2.2) {
        if (f.lit > 0) return { ok: false, msg: 'The fire is already burning' };
        f.lit = BURN_S;
        f.flame.setEnabled(true);
        this._strike(inventory, match);
        this.noise?.campfire(f.x, f.z);
        this.onNet?.({ k: 'fire', x: f.x, y: f.y, z: f.z, stove: f.stove });
        return { ok: true, msg: 'Rekindled the fire' };
      }
    }
    // stove in reach → stove fire
    const stove = (this.buildings?.stoves || []).find((s) =>
      Math.hypot(s.x - playerPos.x, s.z - playerPos.z) < 2.4);
    if (stove) {
      this._place(stove.x, stove.y, stove.z, true, BURN_S);
      this._strike(inventory, match);
      this.noise?.campfire(stove.x, stove.z);
      this.onNet?.({ k: 'fire', x: stove.x, y: stove.y, z: stove.z, stove: true });
      return { ok: true, msg: 'Lit a fire in the stove' };
    }
    // otherwise: campfire on open outdoor ground just ahead of the player
    const px = playerPos.x + Math.sin(heading) * 1.4;
    const pz = playerPos.z + Math.cos(heading) * 1.4;
    if (this.buildings?.floorAt?.(px, pz) != null) {
      return { ok: false, msg: 'No open flames indoors — find a stove' };
    }
    const y = this.terrain.getHeight(px, pz);
    if (y < 0.3) return { ok: false, msg: 'Too wet here' };
    this._place(px, y, pz, false, BURN_S);
    this._strike(inventory, match);
    this.noise?.campfire(px, pz);
    this.onNet?.({ k: 'fire', x: px, y, z: pz, stove: false });
    return { ok: true, msg: 'Lit a campfire' };
  }

  // the box of matches in the player's pockets, or null
  _findMatch(inv) {
    if (!inv?.containers) return null;
    for (const c of inv.containers()) {
      for (const it of c.items) if (it.id === 'matches') return it;
    }
    return null;
  }

  // one strike: the box is used up and the fire makes the noise of catching.
  // ITEMS.matches is stack:1 and Inventory.toJSON only persists
  // {uid,id,x,y,qty}, so a per-box use counter would silently refill on every
  // reconnect — one box, one fire, until matches get a persisted `uses` field.
  _strike(inv, match) {
    if (match) inv.removeItem(match, 1);
    this._ignite();
  }

  // The strike itself. There is no match, lighter or ignition take anywhere in
  // the CC0 harvest (tools/curate-sfx.mjs maps every query in
  // /var/www/sounds/freesound and not one of them is a fire), which is why the
  // old 'fire.light' role never existed in sfx-index.json and lighting a fire
  // was silent. A cigarette hiss or a twig snap in its place would be worse
  // than silence, so it is synthesised the way src/core/ambience.js does its
  // creaks: a bright scrape, then a low swell as the kindling catches.
  // Returns false when the audio context is not running yet.
  _ignite() {
    const c = this.sfx?.audio?.ctx;
    if (!c || c.state !== 'running') return false;
    const master = this.sfx.audio.master;
    if (!this._noise) { // one second of noise, reused by every strike
      this._noise = c.createBuffer(1, c.sampleRate, c.sampleRate);
      const d = this._noise.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
    const layer = (t0, dur, type, f0, f1, peak, q = 1) => {
      const src = c.createBufferSource();
      src.buffer = this._noise;
      src.loop = true;
      const bq = c.createBiquadFilter();
      bq.type = type;
      bq.Q.value = q;
      bq.frequency.setValueAtTime(f0, t0);
      bq.frequency.exponentialRampToValueAtTime(f1, t0 + dur);
      const g = c.createGain();
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(peak, t0 + Math.min(0.03, dur * 0.25));
      g.gain.exponentialRampToValueAtTime(0.0005, t0 + dur);
      src.connect(bq); bq.connect(g); g.connect(master);
      src.start(t0, Math.random() * 0.5);
      src.stop(t0 + dur + 0.02);
    };
    const t = c.currentTime;
    layer(t, 0.09, 'bandpass', 5200, 2600, 0.22, 1.4);   // the scrape
    layer(t + 0.07, 0.65, 'lowpass', 900, 260, 0.14);    // the kindling catching
    return true;
  }

  // multiplayer: someone lit or placed a fire ('left' = seconds still burning)
  applyFire({ x, y, z, stove, left = BURN_S }) {
    const f = this.fires.find((f2) => Math.hypot(f2.x - x, f2.z - z) < 0.5);
    if (f) {
      f.lit = left;
      f.flame.setEnabled(left > 0);
    } else {
      this._place(x, y, z, !!stove, left);
    }
  }

  // lit fire within cooking reach?
  nearFire(pos) {
    return this.fires.find((f) => f.lit > 0 && Math.hypot(f.x - pos.x, f.z - pos.z) < 2.4) || null;
  }

  update(dt) {
    // the steady beacon (see CRACKLE_S)
    this._crackleT -= dt;
    if (this._crackleT <= 0) {
      this._crackleT = CRACKLE_S;
      let n = 0;
      for (const f of this.fires) {
        if (f.lit <= 0) continue;
        this.noise?.campfire(f.x, f.z);
        if (++n >= CRACKLE_MAX) break;
      }
    }
    for (const f of this.fires) {
      if (f.lit <= 0) continue;
      f.lit -= dt;
      // flicker
      const s = 0.85 + Math.sin(performance.now() / 90 + f.x) * 0.12 + Math.random() * 0.06;
      f.flame.scaling.set(s, 0.8 + s * 0.3, s);
      if (f.lit <= 0) f.flame.setEnabled(false); // embers stay as a relightable ring
    }
  }
}
